import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

/** The enclosing `Dialog`'s state, for content that held a close back to ask first. */
const DialogStateContext = React.createContext<{ open: boolean; close: () => void } | null>(null);

/**
 * Radix's `Root`, with the open state always run from here so `DialogContent`
 * can finish a close it held back. To callers it is still `Root`: controlled
 * through `open`/`onOpenChange`, or uncontrolled from `defaultOpen`.
 */
export function Dialog({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const controlled = openProp !== undefined;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );
  const open = controlled ? openProp : uncontrolledOpen;
  const state = React.useMemo(
    () => ({ open, close: () => handleOpenChange(false) }),
    [open, handleOpenChange],
  );
  return (
    <DialogStateContext.Provider value={state}>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props} />
    </DialogStateContext.Provider>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  hideClose?: boolean;
  /**
   * Whether closing now would throw away something the user typed. Left unset,
   * the dialog works it out: it counts as edited from the first change the user
   * makes to a field in it. Pass it where that guess is wrong — a dialog whose
   * fields are a search box or toggles that save on the spot, or whose draft
   * empties itself after each save.
   */
  unsavedChanges?: boolean;
}

/**
 * A click on the backdrop, the X or Escape closes the dialog — easy to do by
 * accident halfway through a long form. Once the dialog holds unsaved input,
 * those three ask before closing. A Cancel button in the dialog is a deliberate
 * close and still closes straight away.
 */
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      hideClose,
      unsavedChanges,
      onChange,
      onPointerDownOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const dialog = React.useContext(DialogStateContext);
    const [edited, setEdited] = React.useState(false);
    const [confirmingClose, setConfirmingClose] = React.useState(false);
    const guarded = dialog !== null && (unsavedChanges ?? edited);

    // Radix unmounts what is in the portal on close, but not this component, so
    // the next opening would inherit the last one's edits without this reset.
    const open = dialog?.open ?? false;
    React.useEffect(() => {
      if (open) return;
      setEdited(false);
      setConfirmingClose(false);
    }, [open]);

    const holdClose = (event: { preventDefault: () => void }) => {
      if (!guarded) return;
      event.preventDefault();
      setConfirmingClose(true);
    };

    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/40 data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(92vw,520px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-xl focus:outline-none',
            className,
          )}
          // React's change event bubbles through portals too, so a field in a
          // dialog rendered inside this one counts here as well: what is entered
          // there usually lands in this dialog's draft.
          onChange={(event) => {
            onChange?.(event);
            setEdited(true);
          }}
          onPointerDownOutside={(event) => {
            onPointerDownOutside?.(event);
            if (event.defaultPrevented) return;
            // Radix never closes a modal on a right-click (or macOS's ctrl-click),
            // so there is nothing to ask about.
            const { button, ctrlKey } = event.detail.originalEvent;
            if (button === 2 || (button === 0 && ctrlKey)) return;
            holdClose(event);
          }}
          onEscapeKeyDown={(event) => {
            onEscapeKeyDown?.(event);
            if (!event.defaultPrevented) holdClose(event);
          }}
          {...props}
        >
          {children}
          {!hideClose && (
            <DialogPrimitive.Close
              className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              aria-label={t('common.close')}
              onClick={holdClose}
            >
              <X className="h-4 w-4" aria-hidden="true" focusable="false" />
            </DialogPrimitive.Close>
          )}
          {confirmingClose && (
            <UnsavedChangesPrompt
              onStay={() => setConfirmingClose(false)}
              onLeave={() => {
                setConfirmingClose(false);
                dialog?.close();
              }}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  },
);

/**
 * Nested inside the dialog it guards, so Radix stacks it on top: Escape or a
 * click outside it dismisses only the prompt, which is the same as staying.
 * "Stay" comes first and so takes the focus — Enter on a reflex keeps the work.
 */
function UnsavedChangesPrompt({
  onStay,
  onLeave,
}: {
  onStay: () => void;
  onLeave: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(open) => !open && onStay()}>
      <DialogContent hideClose unsavedChanges={false} className="w-[min(92vw,420px)]">
        <DialogHeader>
          <DialogTitle>{t('common.unsavedChanges.title')}</DialogTitle>
          <DialogDescription>{t('common.unsavedChanges.description')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onStay}>
            {t('common.unsavedChanges.stay')}
          </Button>
          <Button variant="destructive" onClick={onLeave}>
            {t('common.unsavedChanges.leave')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
DialogContent.displayName = 'DialogContent';

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 space-y-1.5', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';
